import {
  AlertCircleIcon,
  ChevronDownIcon,
  XIcon,
} from '@langchain/untitled-ui-icons';

import { Tooltip } from '@/design-system';
import { ResourceTag, ResourceTagValue } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { TextOverflowTooltip } from '../TextOverflowTooltip';

export function SingleResourceTag(props: {
  tag_key: string;
  tag_value: string;
  gap?: number;
  maxWidth?: number;
  onRemove?: () => void;
  isMismatched?: boolean;
}) {
  const CONTENT_THRESHOLD_CHARS = 15;
  const isKeyLonger = props.tag_key.length > props.tag_value.length;
  const isBothLonger =
    props.tag_key.length > CONTENT_THRESHOLD_CHARS &&
    props.tag_value.length > CONTENT_THRESHOLD_CHARS;

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-full border py-1 pl-3 pr-2 text-sm',
        `gap-${props.gap}`,
        props.tag_key.toLowerCase() === 'application'
          ? 'border-purple bg-purple'
          : 'border-primary bg-tertiary',
        props.isMismatched ? 'opacity-80' : ''
      )}
      style={{ maxWidth: props.maxWidth }}
    >
      <TextOverflowTooltip
        className={cn(
          'text-left font-semibold',
          props.tag_key.toLowerCase() === 'application'
            ? 'text-purple'
            : 'text-secondary',
          isBothLonger ? 'flex-1' : isKeyLonger ? 'flex-grow' : ''
        )}
      >
        {props.tag_key}
      </TextOverflowTooltip>
      <div
        className={cn(
          'flex items-center justify-center gap-1 overflow-hidden rounded-full bg-background px-2 py-0.5',
          props.tag_key.toLowerCase() === 'application'
            ? 'border-purple'
            : 'border-primary text-primary',
          isBothLonger
            ? 'flex-[3_3_0%]'
            : !isKeyLonger
            ? 'flex-grow'
            : 'flex-shrink-0'
        )}
      >
        <TextOverflowTooltip>{props.tag_value}</TextOverflowTooltip>
        {props.onRemove && (
          <button type="button" onClick={props.onRemove}>
            <XIcon className="size-4" />
          </button>
        )}
      </div>
      {props.isMismatched && (
        <Tooltip title={'Mismatched tag'} side="top">
          <div>
            <AlertCircleIcon className="size-4 flex-none" />
          </div>
        </Tooltip>
      )}
    </div>
  );
}

export function SingleKey(props: {
  tag_key: string;
  tags: ResourceTag[];
  values: ResourceTagValue[];
  onRemove?: (tag: ResourceTag) => void;
  canSelect: boolean;
  onNewValue: () => void;
  gap?: number;
  maxWidth?: number;
  hasMismatch?: boolean;
}) {
  const hasValues = props.values.length > 0;
  const hasSelected = props.tags.length > 0;
  const hasMoreThanOneSelected = props.tags.length > 1;

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-full border py-1 pl-3 pr-2 text-xs',
        `gap-${props.gap}`,
        props.tag_key.toLowerCase() === 'application'
          ? 'border-purple bg-purple'
          : 'border-primary bg-tertiary',
        props.hasMismatch ? 'opacity-80' : ''
      )}
      style={{ maxWidth: props.maxWidth }}
    >
      <TextOverflowTooltip
        className={cn(
          'flex-1 text-left font-semibold',
          props.tag_key.toLowerCase() === 'application'
            ? 'text-purple'
            : 'text-secondary'
        )}
      >
        {props.tag_key}
      </TextOverflowTooltip>

      <div
        className={cn(
          'flex max-w-[65%] items-center justify-center gap-1 rounded-full bg-background px-2 py-0.5',
          props.tag_key.toLowerCase() === 'application'
            ? 'border-purple'
            : 'border-primary text-primary'
        )}
      >
        {hasSelected ? (
          <>
            <TextOverflowTooltip>{props.tags[0].tag_value}</TextOverflowTooltip>

            {props.onRemove && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  props.onRemove?.(props.tags[0]);
                }}
                data-testid={`${props.tag_key}-${props.tags[0].tag_value}-remove`}
              >
                <XIcon className="size-4" />
              </button>
            )}
          </>
        ) : hasValues ? (
          props.canSelect ? (
            'Select'
          ) : (
            'None'
          )
        ) : (
          <button type="button" onClick={() => props.onNewValue()}>
            Set up
          </button>
        )}
      </div>

      {hasMoreThanOneSelected && (
        <div
          className={cn(
            'flex max-w-[45%] items-center justify-center gap-1 rounded-full bg-background px-2 py-0.5',
            props.tag_key.toLowerCase() === 'application'
              ? 'border-purple'
              : 'border-primary text-primary'
          )}
        >
          +{props.tags.length - 1}
        </div>
      )}
      {hasValues &&
        (props.canSelect ? (
          <ChevronDownIcon className="size-4 flex-none" />
        ) : props.hasMismatch ? (
          <AlertCircleIcon className="size-4 flex-none" />
        ) : null)}
    </div>
  );
}
